import HeroSearch from "../components/HeroSearch";
import { Link } from "react-router-dom";
import { Search, FileText, Lock, ArrowRight, ShieldCheck, Database, Phone, FileSignature, CheckCircle2, XCircle, AlertTriangle, Globe2, Building2, Calendar, Scale } from "lucide-react";
import { EditableText } from "../lib/content";
import { useSeo } from "../lib/useSeo";

const STEPS = [
  {
    n: "01",
    icon: Search,
    title: "Check Your Name",
    body: "Find out if your business name could create problems.",
    micro: [],
  },
  {
    n: "02",
    icon: FileText,
    title: "Understand the Risk",
    body: "See potential conflicts and get a simple risk score.",
    micro: [],
  },
  {
    n: "03",
    icon: Lock,
    title: "Protect Your Position",
    body: "Create evidence of use and gain support if a dispute arises.",
    micro: [],
  },
];

const ONGOING = [
  { icon: Database, title: "Your claim is recorded", body: "We create a dated record of your business name and trading history, giving you evidence of use from day one." },
  { icon: Phone, title: "Real people to help", body: "Questions or concerns? Our UK team is available by phone and email when you need support." },
  { icon: FileSignature, title: "Support if a dispute arises", body: "If someone challenges your business name, we'll help you understand your options and respond appropriately." },
  { icon: ShieldCheck, title: "Building evidence over time", body: "Your record continues to grow month after month, helping strengthen your position as your business develops." },
];

export default function HowItWorks() {
  useSeo({
    title: "How Business Name Protection Works",
    description: "Three simple steps: check your name, receive your risk report, and get protected. UK business name protection from £15/mo, with real legal backing.",
  });

  return (
    <main data-testid="how-it-works-page">
      <section className="relative overflow-hidden bnp-radial-glow pt-14 md:pt-20 pb-12">
        <div className="absolute inset-0 bnp-grid-bg opacity-30 pointer-events-none" />
        <div className="max-w-7xl mx-auto px-5 sm:px-8 lg:px-12 xl:px-16 2xl:px-24 relative">
          <div className="text-center max-w-3xl mx-auto">
            <span className="bnp-chip bnp-chip-teal">How it works</span>
            <h1 className="font-display text-[clamp(2rem,4vw,3.4rem)] text-[#04273d] mt-4 font-semibold tracking-tight leading-[1.05]">
              <EditableText keyName="how_it_works.hero.title" default="Search. Receive. Protect." />
            </h1>
            <p className="text-[#447694] text-lg mt-4 leading-relaxed">
              <EditableText keyName="how_it_works.hero.body" default="Three simple steps. Under a minute. Built for owners who don't have an afternoon to read legal documentation." />
            </p>
            <div className="mt-8 max-w-2xl mx-auto">
              <HeroSearch inline />
            </div>
          </div>
        </div>
      </section>

      {/* STEPS DETAIL */}
      <section className="py-16 md:py-24 lg:py-28 xl:py-32">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 lg:px-12 xl:px-16 2xl:px-24 space-y-10">
          {STEPS.map((s, i) => (
            <div key={s.n} className={`bnp-card overflow-hidden grid lg:grid-cols-2 gap-0 ${i % 2 === 1 ? "lg:[&>*:first-child]:order-2" : ""}`} data-testid={`hiw-step-${i}`}>
              <div className="p-8 md:p-10 lg:p-12">
                <div className="flex items-center gap-3">
                  <span className="font-display text-xs font-semibold tracking-widest uppercase text-[#447694]">Step {s.n}</span>
                  <span className="h-px flex-1 bg-[#9ac7f7]/40" />
                </div>
                <div className="h-12 w-12 rounded-2xl bg-[#71edc9]/30 flex items-center justify-center mt-5">
                  <s.icon size={22} className="text-[#04273d]" />
                </div>
                <h2 className="font-display text-2xl md:text-3xl text-[#04273d] mt-5 font-semibold tracking-tight">{s.title}</h2>
                <p className="text-[#447694] mt-3 leading-relaxed">{s.body}</p>
                {s.micro && s.micro.length > 0 && (
                  <ul className="mt-5 space-y-2">
                    {s.micro.map((m) => (
                      <li key={m} className="flex items-start gap-2 text-sm text-[#447694]">
                        <ShieldCheck size={14} className="text-[#71edc9] mt-0.5 shrink-0" />
                        <span>{m}</span>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
              <div className="bg-gradient-to-br from-[#04273d] to-[#0a3b59] p-8 md:p-10 lg:p-12 flex flex-col justify-center min-h-[320px]">
                {i === 0 && (
                  /* STEP 1 - search bar mockup matching the real product */
                  <div className="w-full" data-testid="hiw-visual-1">
                    <p className="font-mono-brand text-[10px] text-[#9ac7f7] tracking-widest uppercase mb-3">Free name check</p>
                    <div className="bg-white rounded-full p-2 flex items-center gap-2 shadow-2xl">
                      <Search size={18} className="text-[#447694] ml-3" />
                      <span className="text-[#04273d] font-medium flex-1 text-sm">Crown Hair Studio</span>
                      <span className="bg-[#71edc9] text-[#04273d] font-display font-bold text-xs px-5 py-2.5 rounded-full inline-flex items-center gap-1.5">
                        <ShieldCheck size={13} /> Check
                      </span>
                    </div>
                    <div className="mt-4 flex items-center gap-2 text-[10px] font-mono-brand text-[#9ac7f7]/80 uppercase tracking-wider">
                      <span className="inline-block h-1.5 w-1.5 rounded-full bg-[#71edc9] animate-pulse" />
                      Cross-referencing 50+ UK records across 5 TLDs
                    </div>
                    <div className="grid grid-cols-2 gap-2 mt-5">
                      {[
                        { Icon: Building2, label: "Companies House" },
                        { Icon: Globe2,    label: "5 domains" },
                      ].map(({ Icon, label }) => (
                        <div key={label} className="bg-white/5 border border-white/10 rounded-xl p-3 text-center">
                          <Icon size={14} className="text-[#71edc9] mx-auto" />
                          <p className="text-[10px] text-white/85 mt-1.5 leading-tight">{label}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                {i === 1 && (
                  /* STEP 2 - report card mockup matching the real /check page */
                  <div className="w-full" data-testid="hiw-visual-2">
                    <div className="flex items-start justify-between gap-3 mb-3">
                      <div>
                        <p className="font-mono-brand text-[10px] text-[#9ac7f7] tracking-widest uppercase">Your risk report</p>
                        <p className="font-display text-white font-bold text-base mt-1">Crown Hair Studio</p>
                      </div>
                      <div className="text-right">
                        <p className="font-display text-3xl font-bold text-amber-300 leading-none">68<span className="text-white/50 text-sm font-normal">/100</span></p>
                        <span className="inline-block bg-amber-300/20 text-amber-300 text-[10px] font-bold px-2 py-0.5 rounded-full mt-1">HIGH RISK</span>
                      </div>
                    </div>
                    <div className="h-1.5 rounded-full bg-white/10 overflow-hidden">
                      <div className="h-full bg-amber-300 rounded-full" style={{ width: "68%" }} />
                    </div>
                    <div className="grid grid-cols-2 gap-2 mt-4">
                      <div className="bg-white/5 border border-white/10 rounded-xl p-2.5">
                        <p className="text-[9px] text-white/60 uppercase tracking-wider">Companies House</p>
                        <p className="text-[11px] text-white mt-1.5 truncate">Crown Hair &amp; Beauty</p>
                        <p className="text-[10px] text-red-300 font-bold">94% match</p>
                      </div>
                      <div className="bg-white/5 border border-white/10 rounded-xl p-2.5">
                        <p className="text-[9px] text-white/60 uppercase tracking-wider">Domains</p>
                        <p className="text-[11px] text-white mt-1.5 truncate">.co.uk &middot; .com</p>
                        <p className="text-[10px] text-red-300 font-bold flex items-center gap-1"><XCircle size={9} /> Taken</p>
                      </div>
                    </div>
                    <div className="mt-3 bg-[#71edc9]/15 border border-[#71edc9]/30 rounded-xl p-3">
                      <div className="flex items-start gap-2">
                        <AlertTriangle size={13} className="text-[#71edc9] mt-0.5 shrink-0" />
                        <p className="text-[10px] text-white leading-relaxed">
                          <span className="font-bold text-[#71edc9]">Top recommendation:</span> Get Business Name Protection in place today &mdash; you have 2 near-identical UK conflicts.
                        </p>
                      </div>
                    </div>
                  </div>
                )}
                {i === 2 && (
                  /* STEP 3 - "Protected" confirmation card mockup */
                  <div className="w-full" data-testid="hiw-visual-3">
                    <div className="bg-white/5 border border-[#71edc9]/30 rounded-2xl p-5">
                      <div className="flex items-center gap-3">
                        <div className="h-12 w-12 rounded-full bg-[#71edc9] flex items-center justify-center shrink-0">
                          <ShieldCheck size={22} className="text-[#04273d]" />
                        </div>
                        <div>
                          <p className="text-[10px] font-mono-brand text-[#9ac7f7] tracking-widest uppercase">Status</p>
                          <p className="font-display text-white font-bold text-base">Protected</p>
                        </div>
                        <div className="ml-auto bg-[#71edc9]/20 border border-[#71edc9]/40 px-2.5 py-1 rounded-full">
                          <p className="text-[10px] text-[#71edc9] font-bold tracking-wider uppercase">ACTIVE</p>
                        </div>
                      </div>
                      <div className="border-t border-white/10 mt-4 pt-4 space-y-2.5">
                        <div className="flex items-center justify-between text-[11px]">
                          <span className="text-white/60 flex items-center gap-1.5"><Calendar size={11} /> Protection started</span>
                          <span className="text-white font-medium">Today</span>
                        </div>
                        <div className="flex items-center justify-between text-[11px]">
                          <span className="text-white/60 flex items-center gap-1.5"><FileText size={11} /> Dated proof of use</span>
                          <span className="text-emerald-300 font-bold flex items-center gap-1"><CheckCircle2 size={10} /> Recording</span>
                        </div>
                        <div className="flex items-center justify-between text-[11px]">
                          <span className="text-white/60 flex items-center gap-1.5"><Scale size={11} /> Passing-off cover</span>
                          <span className="text-[#71edc9] font-bold">Up to £10,000</span>
                        </div>
                        <div className="flex items-center justify-between text-[11px]">
                          <span className="text-white/60 flex items-center gap-1.5"><Phone size={11} /> UK team</span>
                          <span className="text-white">On call, Mon&ndash;Fri</span>
                        </div>
                      </div>
                    </div>
                    <p className="text-[10px] font-mono-brand text-[#9ac7f7]/70 mt-3 text-center tracking-wider uppercase">£15/month including VAT &middot; Cancel anytime</p>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* AFTER YOU SUBSCRIBE */}
      <section className="py-16 md:py-20 lg:py-24 xl:py-28 bg-white border-y border-[#9ac7f7]/25" data-testid="hiw-ongoing">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 lg:px-12 xl:px-16 2xl:px-24">
          <div className="max-w-xl mb-10">
            <span className="bnp-chip bnp-chip-light">After you subscribe</span>
            <h2 className="font-display text-[clamp(1.75rem,3.2vw,3rem)] text-[#04273d] mt-3 font-semibold tracking-tight">
              What happens on day one and beyond.
            </h2>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
            {ONGOING.map((o, i) => (
              <div key={i} className="bnp-card p-6" data-testid={`hiw-ongoing-${i}`}>
                <div className="h-10 w-10 rounded-xl bg-[#04273d] flex items-center justify-center">
                  <o.icon size={18} className="text-[#71edc9]" />
                </div>
                <h3 className="font-display text-lg text-[#04273d] mt-4 font-semibold">{o.title}</h3>
                <p className="text-sm text-[#447694] mt-2 leading-relaxed">{o.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20" data-testid="hiw-cta">
        <div className="max-w-3xl mx-auto px-5 sm:px-8 lg:px-12 xl:px-16 2xl:px-24 text-center">
          <h2 className="font-display text-[clamp(1.75rem,3.5vw,3rem)] text-[#04273d] font-bold tracking-tight">
            <EditableText keyName="how_it_works.cta.title" default="Don't wait for a name dispute to find out there's a problem." />
          </h2>
          <p className="text-[#447694] mt-4 text-lg">
            <EditableText keyName="how_it_works.cta.body" default="Check your business name free and see your risk score instantly." />
          </p>
          <div className="mt-7 flex flex-wrap items-center justify-center gap-3">
            <Link to="/check" className="btn-primary" data-testid="hiw-cta-check-button">Check My Name Free <ArrowRight size={14} /></Link>
            <Link to="/pricing" className="btn-secondary" data-testid="hiw-cta-pricing-link">See pricing</Link>
          </div>
        </div>
      </section>
    </main>
  );
}
